import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ArrowUpRight } from 'lucide-react';
import { PROJECTS } from '../data';
import Navigation from '../components/organisms/Navigation';
import Footer from '../components/organisms/Footer';
import PageTransition from '../components/templates/PageTransition';
import SEO from '../components/utils/SEO';

const Work = () => {
    const projectRefs = useRef([]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // GSAP Hover Animation
    const handleMouseEnter = (index) => {
        const el = projectRefs.current[index];
        const img = el.querySelector('img');
        const overlay = el.querySelector('.overlay');

        gsap.to(overlay, {
            opacity: 1,
            duration: 0.4,
            ease: "power2.out"
        });
        gsap.to(img, {
            scale: 1.1,
            duration: 0.8,
            ease: "power2.out"
        });
    };

    const handleMouseLeave = (index) => {
        const el = projectRefs.current[index];
        const img = el.querySelector('img');
        const overlay = el.querySelector('.overlay');

        gsap.to(overlay, {
            opacity: 0,
            duration: 0.4,
            ease: "power2.out"
        });
        gsap.to(img, {
            scale: 1,
            duration: 0.8,
            ease: "power2.out"
        });
    };

    const handleMouseMove = (e, index) => {
        const el = projectRefs.current[index];
        const viewBtn = el.querySelector('.view-btn');
        const rect = el.getBoundingClientRect();

        // Calculate relative position inside the card
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Move the button towards the cursor with some lag/smoothing
        gsap.to(viewBtn, {
            x: x - 50, // center the button (assuming 100px width)
            y: y - 50,
            duration: 0.3,
            ease: "power2.out"
        });
    };

    return (
        <PageTransition>
            <SEO
                title="Selected Works"
                description="A curated selection of high-impact digital products and websites built for ambitious founders."
            />
            <div className="bg-[#fbfbf4] text-[#050505] font-sans selection:bg-[#050505] selection:text-[#fbfbf4] min-h-screen">
                <Navigation />

                <main className="pt-32 pb-20 overflow-hidden">
                    <section className="px-4 md:px-8 mb-4 border-b border-black/10 pb-4 flex justify-between items-end">
                        <span className="text-xs uppercase tracking-[0.2em] opacity-50">Selected Works</span>
                        <div className="text-xs uppercase tracking-[0.2em] space-x-8 hidden md:block">
                            <span>2024</span>
                            <span>—</span>
                            <span>2026</span>
                        </div>
                    </section>
                    <div className="px-2 md:px-6 mb-20 md:mb-32">
                        <h1 className="font-sans font-black text-[15vw] leading-[0.8] tracking-tighter uppercase text-center md:text-left">
                            Selected
                            <span className="block text-right opacity-30">Works.</span>
                        </h1>
                    </div>

                    <div className="max-w-[95vw] mx-auto grid grid-cols-12 gap-y-24 md:gap-y-48 px-4 md:px-0">
                        {PROJECTS.map((project, i) => {
                            // Asymmetrical Layout Logic
                            const isEven = i % 2 === 0;
                            const gridClass = isEven
                                ? "col-span-12 md:col-span-7 md:col-start-1"
                                : "col-span-12 md:col-span-7 md:col-start-6";

                            // Stagger Vertical Position for visual interest
                            const marginTop = i > 0 ? (isEven ? "-mt-0 md:-mt-32" : "-mt-0 md:mt-12") : "";

                            return (
                                <div
                                    key={project.id}
                                    className={`${gridClass} ${marginTop} relative group`}
                                    ref={el => projectRefs.current[i] = el}
                                    onMouseEnter={() => handleMouseEnter(i)}
                                    onMouseLeave={() => handleMouseLeave(i)}
                                    onMouseMove={(e) => handleMouseMove(e, i)}
                                >
                                    <Link to={`/work/${project.id}`} className="block relative overflow-hidden cursor-none">
                                        {/* Image Container with Reveal Effect */}
                                        <div className="relative aspect-[4/3] overflow-hidden rounded-sm">
                                            <div className="overlay absolute inset-0 bg-black/20 z-10 opacity-0 transition-opacity"></div>
                                            <img
                                                src={project.image}
                                                alt={project.title}
                                                className="w-full h-full object-cover transition-transform will-change-transform"
                                            />

                                            {/* GSAP Floater Button */}
                                            <div className="view-btn absolute top-0 left-0 w-24 h-24 bg-[#050505] text-[#fbfbf4] rounded-full flex items-center justify-center pointer-events-none z-20 scale-0 group-hover:scale-100 transition-transform duration-300">
                                                <div className="text-center">
                                                    <span className="block text-[10px] uppercase tracking-widest font-bold">View</span>
                                                    <ArrowUpRight size={16} className="mx-auto mt-1" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Project Info - Layout Different from Standard Card */}
                                        <div className="mt-8 flex flex-col md:flex-row md:items-start justify-between gap-6">
                                            <div className="max-w-md">
                                                <span className="block text-xs uppercase tracking-[0.2em] opacity-50 mb-3">
                                                    {String(i + 1).padStart(2, '0')} — {project.client}
                                                </span>
                                                <h2 className="font-migra text-4xl md:text-5xl group-hover:italic transition-all duration-300 mb-4">{project.title}</h2>
                                                <p className="text-sm opacity-60 leading-relaxed max-w-xs">{project.description.slice(0, 120)}...</p>
                                            </div>
                                            <div className="flex flex-col items-end text-right md:text-right">
                                                <span className="inline-block px-3 py-1 border border-black/10 rounded-full text-xs uppercase tracking-widest opacity-60 mb-2">
                                                    {project.category}
                                                </span>
                                                <span className="text-xs font-mono opacity-40">{project.year}</span>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            );
                        })}
                    </div>
                </main>

                <Footer />
            </div>
        </PageTransition>
    );
};

export default Work;
